import React, { useState } from "react";
import { Head, router } from "@inertiajs/react";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Edit, Trash2, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";

interface Tag {
  id: number;
  name: string;
  color: string;
  position: number;
  subject_lines_count: number;
  templates_count: number;
  created_at: string;
}

interface TagsProps {
  tags: Tag[];
}

function Tags({ tags }: TagsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    color: "green",
    position: 0,
  });
  const [loading, setLoading] = useState(false);

  const colorOptions = [
    { value: "green", label: "Green", className: "bg-green-100 text-green-800" },
    { value: "yellow", label: "Yellow", className: "bg-yellow-100 text-yellow-800" },
    { value: "red", label: "Red", className: "bg-red-100 text-red-800" },
    { value: "blue", label: "Blue", className: "bg-blue-100 text-blue-800" },
    { value: "purple", label: "Purple", className: "bg-purple-100 text-purple-800" },
    { value: "gray", label: "Gray", className: "bg-gray-100 text-gray-800" },
  ];

  const getColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-800 border-green-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "red":
        return "bg-red-100 text-red-800 border-red-200";
      case "blue":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "purple":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "gray":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleAdd = () => {
    setSelectedTag(null);
    setFormData({
      name: "",
      color: "green",
      position: tags.length,
    });
    setDialogOpen(true);
  };

  const handleEdit = (tag: Tag) => {
    setSelectedTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      position: tag.position,
    });
    setDialogOpen(true);
  };

  const handleDelete = (tag: Tag) => {
    // Check if tag has associated subject lines or templates
    if (tag.subject_lines_count > 0 || tag.templates_count > 0) {
      const associations = [];
      if (tag.subject_lines_count > 0) {
        associations.push(`${tag.subject_lines_count} subject line${tag.subject_lines_count > 1 ? 's' : ''}`);
      }
      if (tag.templates_count > 0) {
        associations.push(`${tag.templates_count} template${tag.templates_count > 1 ? 's' : ''}`);
      }
      alert(`Cannot delete tag "${tag.name}" because it has ${associations.join(' and ')}.`);
      return;
    }
    
    setTagToDelete(tag);
    setConfirmDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const data = { tag: formData };

    if (selectedTag) {
      router.patch(`/tags/${selectedTag.id}`, data, {
        onSuccess: () => {
          toast.success("Tag updated successfully");
          setDialogOpen(false);
        },
        onError: () => {
          toast.error("Failed to update tag");
        },
        onFinish: () => {
          setLoading(false);
        },
      });
    } else {
      router.post("/tags", data, {
        onSuccess: () => {
          toast.success("Tag created successfully");
          setDialogOpen(false);
        },
        onError: () => {
          toast.error("Failed to create tag");
        },
        onFinish: () => {
          setLoading(false);
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (tagToDelete) {
      router.delete(`/tags/${tagToDelete.id}`, {
        onSuccess: () => {
          toast.success("Tag deleted successfully");
          setConfirmDialogOpen(false);
          setTagToDelete(null);
        },
        onError: () => {
          toast.error("Failed to delete tag");
        },
      });
    }
  };


  return (
    <>
      <Head title="Tags | Mailkit - Resolute" />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            Manage Tags
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage tags for subject lines and templates
          </p>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-lg font-medium text-gray-900">
              Tags
            </CardTitle>
            <Button 
              onClick={handleAdd} 
              size="sm"
              className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tag
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preview
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tags.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <div className="rounded-full bg-gray-100 p-3 mb-4">
                            <TagIcon className="w-6 h-6 text-gray-400" />
                          </div>
                          <p className="text-gray-500 text-sm">No tags created yet</p>
                          <p className="text-gray-400 text-xs mt-1">
                            Click "Add Tag" to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    tags.map((tag, index) => (
                      <tr key={tag.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {tag.name}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorClass(tag.color)}`}>
                            {tag.name}
                          </span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(tag)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(tag)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>
                  {selectedTag ? "Edit Tag" : "Add Tag"}
                </DialogTitle>
                <DialogDescription>
                  {selectedTag
                    ? "Update the tag details."
                    : "Create a new tag for categorizing content."}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter tag name..."
                    required
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="color">Color *</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData({ ...formData, color: value })}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {formData.color && (
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full ${
                              formData.color === 'green' ? 'bg-green-500' :
                              formData.color === 'yellow' ? 'bg-yellow-500' :
                              formData.color === 'red' ? 'bg-red-500' :
                              formData.color === 'blue' ? 'bg-blue-500' :
                              formData.color === 'purple' ? 'bg-purple-500' :
                              'bg-gray-500'
                            }`} />
                            <span className="capitalize">{formData.color}</span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full ${
                              option.value === 'green' ? 'bg-green-500' :
                              option.value === 'yellow' ? 'bg-yellow-500' :
                              option.value === 'red' ? 'bg-red-500' :
                              option.value === 'blue' ? 'bg-blue-500' :
                              option.value === 'purple' ? 'bg-purple-500' :
                              'bg-gray-500'
                            }`} />
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                >
                  {loading ? "Saving..." : selectedTag ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onOpenChange={(open) => {
          setConfirmDialogOpen(open);
          if (!open) setTagToDelete(null);
        }}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Delete Tag</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{tagToDelete?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
}

Tags.layout = (page) => <AdminLayout>{page}</AdminLayout>;

export default Tags;